import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Inbox,
  Newspaper,
  FileText,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/blog", label: "Blog Posts", icon: Newspaper },
  { to: "/admin/case-studies", label: "Case Studies", icon: FileText },
  { to: "/admin/chat-sessions", label: "Chat Sessions", icon: MessageSquare },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-ridge-stone" data-testid="admin-layout">
      {/* Mobile top bar */}
      <div className="fixed inset-x-0 top-0 z-30 flex h-14 items-center justify-between border-b border-ridge-border bg-white px-4 lg:hidden">
        <Link to="/admin" className="flex items-center gap-2">
          <img src="/logo-icon.png" alt="" className="h-7 w-7" />
          <span className="font-display text-[0.7rem] font-bold uppercase tracking-[0.2em] text-ridge-charcoal">RIDGEHAWK · ADMIN</span>
        </Link>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ridge-charcoal hover:bg-ridge-stone"
          aria-label="Toggle navigation"
          data-testid="admin-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-20 w-[240px] flex-col border-r border-white/5 bg-ridge-charcoal text-white transition-transform duration-200 lg:flex lg:translate-x-0 ${
          open ? "flex translate-x-0" : "hidden -translate-x-full lg:flex"
        }`}
        aria-label="Admin sidebar"
      >
        <div className="flex h-16 items-center gap-3 border-b border-white/5 px-6">
          <img src="/logo-icon.png" alt="" className="h-8 w-8 rounded bg-white p-0.5" />
          <div>
            <span className="block font-display text-[0.65rem] font-bold uppercase tracking-[0.22em] text-white">
              Ridgehawk
            </span>
            <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-ridge-amber">
              Admin
            </span>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-5" data-testid="admin-nav">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `mb-1 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-white/65 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-white/5 p-3">
          {user ? (
            <div className="mb-3 truncate px-3 py-2 text-[0.65rem] uppercase tracking-[0.18em] text-white/55">
              <span className="block">Signed in as</span>
              <span className="block truncate font-semibold text-white" data-testid="admin-current-user">
                {user.email}
              </span>
            </div>
          ) : null}
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-white/65 transition-colors hover:bg-white/5 hover:text-white"
            data-testid="admin-logout"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Backdrop on mobile */}
      {open ? (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-10 bg-black/40 lg:hidden"
        />
      ) : null}

      {/* Main */}
      <main className="ml-0 flex-1 px-4 pb-16 pt-20 sm:px-6 md:px-8 lg:ml-[240px] lg:pt-8" data-testid="admin-main">
        <Outlet />
      </main>
    </div>
  );
}
